import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Popover } from '@highlight/ui'
import { Popover as BasePopover } from '@base-ui/react/popover'
import { Check } from 'lucide-react'
import { Virtuoso } from 'react-virtuoso'
import type { StagedActionEditTag, StagedActionView } from '@renderer/overlay/Sidepanel/hooks/use-staged-actions'
import { useEditOptions } from './edit-options-context'
import { ActionTagBadgeCollection } from './badge-collection'
import { Icon } from './badge'
import { compareCodeUnits } from '@renderer/utils/string'

const MAX_VISIBLE_HEIGHT = 256

interface EditableMultiSelectProps {
  keyName: string
  tags: Array<StagedActionView['tags'][number]>
  editTag: StagedActionEditTag
}

export function EditableMultiSelect({ keyName, tags, editTag }: EditableMultiSelectProps) {
  const { onUpdate, isUpdating } = useEditOptions()
  const [open, setOpen] = useState(false)
  const [pendingValues, setPendingValues] = useState<Array<string>>([])

  const resolveSelectedValues = useCallback(() => {
    return tags
      .map((tag) => editTag.options.find((o) => o.name === tag.value)?.value)
      .filter((v): v is string => v != null)
  }, [tags, editTag])

  const [initialValues, setInitialValues] = useState<Array<string>>([])

  useEffect(() => {
    if (isUpdating && open) {
      setOpen(false)
    }
  }, [isUpdating, open])

  const handleOpen = useCallback(() => {
    const current = resolveSelectedValues()
    setInitialValues(current)
    setPendingValues(current)
    setOpen(true)
  }, [resolveSelectedValues])

  const handleCancel = useCallback(() => {
    setOpen(false)
  }, [])

  const handleSave = useCallback(() => {
    const tagEdits: Record<string, Array<string>> = { [keyName]: pendingValues }
    if (pendingValues.length > 0 && editTag.constraints?.exclusive) {
      for (const key of editTag.constraints.exclusive) {
        tagEdits[key] = []
      }
    }
    onUpdate({ tagEdits })
    setOpen(false)
  }, [keyName, onUpdate, pendingValues, editTag])

  const maxItems = editTag.constraints?.maxItems
  const atMaxItems = maxItems != null && pendingValues.length >= maxItems
  const minItems = editTag.constraints?.minItems
  const atMinItems = minItems != null && pendingValues.length <= minItems

  const hasChanges = useMemo(() => {
    if (pendingValues.length !== initialValues.length) return true
    const sorted = [...pendingValues].sort(compareCodeUnits)
    const sortedInitial = [...initialValues].sort(compareCodeUnits)
    return sorted.some((v, i) => v !== sortedInitial[i])
  }, [pendingValues, initialValues])

  const handleToggle = useCallback(
    (value: string) => {
      setPendingValues((prev) => {
        const isSelected = prev.includes(value)
        if (isSelected) {
          if (minItems != null && prev.length <= minItems) return prev
          return prev.filter((v) => v !== value)
        }
        if (maxItems != null && prev.length >= maxItems) return prev
        return [...prev, value]
      })
    },
    [maxItems, minItems],
  )

  const displayTags = useMemo(() => {
    if (!open) return tags
    const alwaysShowKey = tags[0]?.alwaysShowKey
    return pendingValues
      .map((value) => {
        const opt = editTag.options.find((o) => o.value === value)
        if (!opt) return null
        return {
          key: keyName,
          value: opt.name,
          icon: opt.icon,
          ...(alwaysShowKey ? { alwaysShowKey: alwaysShowKey } : {}),
        }
      })
      .filter((t): t is NonNullable<typeof t> => t != null)
  }, [open, pendingValues, tags, editTag, keyName])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <BasePopover.Trigger
        render={<div />}
        className={`flex flex-wrap gap-2 ${isUpdating ? 'pointer-events-none opacity-50' : 'cursor-pointer'}`}
        onClick={handleOpen}
      >
        <ActionTagBadgeCollection keyName={keyName} tags={displayTags} editable />
      </BasePopover.Trigger>
      <Popover.Content
        sideOffset={4}
        disableAnchorTracking
        className="flex w-56 flex-col rounded-xl p-0 dark:outline-0"
      >
        <div className="flex items-center justify-between px-2 pt-1.5">
          <span className="text-foreground-secondary text-xs font-medium capitalize">{keyName}</span>
          {maxItems != null && (
            <span className="text-foreground-placeholder text-xs">
              {pendingValues.length}/{maxItems}
            </span>
          )}
        </div>
        <div className="p-1">
          <Virtuoso
            style={{ height: Math.min(editTag.options.length * 32, MAX_VISIBLE_HEIGHT) }}
            totalCount={editTag.options.length}
            itemContent={(index) => {
              const opt = editTag.options[index]
              const isChecked = pendingValues.includes(opt.value)
              return (
                <button
                  type="button"
                  className="text-foreground-secondary hover:bg-element-hover flex w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm"
                  onClick={() => handleToggle(opt.value)}
                  disabled={(!isChecked && atMaxItems) || (isChecked && atMinItems)}
                >
                  <span className="flex flex-1 items-center gap-2 overflow-hidden">
                    <Icon src={opt.icon} className="size-4 shrink-0 rounded" />
                    <span className="truncate">{opt.name}</span>
                  </span>
                  <span className="flex size-4 shrink-0 items-center justify-center">
                    {isChecked && <Check className="size-3" />}
                  </span>
                </button>
              )
            }}
          />
        </div>
        <div className="flex items-center justify-end gap-2 px-2 py-1.5">
          <Button variant="ghost" size="xs" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="primary" size="xs" onClick={handleSave} disabled={!hasChanges}>
            Save
          </Button>
        </div>
      </Popover.Content>
    </Popover>
  )
}
